// shadcn-flavored Button (manual install per ADR-025).
//
// Source pattern: https://ui.shadcn.com/docs/components/button — `default`
// variant family (clipped to the variants we actually use). Adapted for:
//   - TS strict + verbatimModuleSyntax (note `type` keyword imports).
//   - exactOptionalPropertyTypes (asChild defaults to `false`).
//   - SPA-only (no "use client" — Vite has no RSC).
//   - Pre-Frost token bridge (uses bg-primary / text-primary-foreground /
//     bg-destructive / etc. which resolve to our orange/cream tokens).

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-[14px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-orange-deep',
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
        outline:
          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-cream',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-5 py-2.5',
        sm: 'h-8 rounded-sm px-3 text-[13px]',
        lg: 'h-11 rounded-sm px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    // For native <button>, default to type="button" so accidental form submit
    // is impossible (we don't render any portal-side <form> that uses button
    // submission today). Slot passes through whatever the child renders.
    const buttonType = asChild ? undefined : (type ?? 'button');
    return (
      <Comp
        ref={ref}
        type={buttonType}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
